import { PageSpinner, useNav } from '@armhr/ui';
import { Alert, Box, Typography } from '@mui/material';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';

import { useApiData } from '../../hooks/useApiData';
import { useUser } from '../../hooks/useUser';
import { paths } from '../../utils/paths';

const isForbidden = (err: Error | null) =>
  (err as any)?.response?.status === 403;

const SwipeclockSchedulePage: React.FC = () => {
  const { user } = useUser();
  const navigate = useNavigate();
  const { setFluid } = useNav();

  const {
    data: scheduleUrl,
    loading: scheduleLoading,
    error: scheduleError,
  } = useApiData<string | null>((api) => api.attendance.getScheduleRedirect());

  // Render edge-to-edge so the iframe can own the full content height.
  useEffect(() => {
    setFluid(true);
    return () => setFluid(false);
  }, [setFluid]);

  // The Embedded Schedule Manager is gated by the NOSCHED template token.
  if (!user?.is_swipeclock_enabled || !user?.is_swipeclock_schedule_enabled) {
    navigate(paths.dashboard);
    return null;
  }

  if (scheduleLoading) {
    return <PageSpinner />;
  }

  if (isForbidden(scheduleError)) {
    navigate(paths.dashboard);
    return null;
  }

  if (scheduleError || !scheduleUrl) {
    return (
      <Alert severity="error">
        An error occured while loading the schedule. Please refresh and try
        again
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Helmet>
        <title>Schedule | Armhr</title>
        <meta name="description" content="View and manage your schedule." />
      </Helmet>
      <Box sx={{ display: 'flex', px: 3, py: 2, alignItems: 'center' }}>
        <Typography variant="h2">Schedule</Typography>
      </Box>
      <iframe
        src={scheduleUrl}
        title="SwipeClock Schedule"
        loading="lazy"
        style={{
          background: 'white',
          width: '100%',
          height: '100%',
          border: 'none',
        }}
      />
    </Box>
  );
};

export default SwipeclockSchedulePage;
